import { useMemo, useState, type FormEvent } from 'react';
import {
  useCreateCeoInflowPlanMutation,
  useGetCeoInflowsQuery,
  useUpdateCeoInflowFactMutation,
  type CeoInflowPlanRow,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { TreasuryViewNote, useTreasuryWrite } from '../treasuryAccess';
import { HeaderActions } from '../ui';
import { AddInflowPlanModal } from './AddInflowPlanModal';
import { formatPeriodMonthLabel } from './inflowHelpers';
import { InflowsFunnelGrid } from './InflowsFunnelGrid';
import { InflowsTable } from './InflowsTable';
import { SmetaKs3ActsTable } from './SmetaKs3ActsTable';
import {
  ErrorMessage,
  ErrorRow,
  HeaderRow,
  Section,
  Select,
  SubTitleText,
  Title,
} from './styles';
import type { InflowsPageProps } from './types';

export function CeoInflowsView({ editable = false }: InflowsPageProps) {
  const can_write = useTreasuryWrite(editable);
  const [periodFilter, setPeriodFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const { data, isLoading, isError, refetch } = useGetCeoInflowsQuery(
    periodFilter || statusFilter
      ? {
          period_month: periodFilter || undefined,
          status: statusFilter || undefined,
        }
      : undefined,
  );
  const [createInflow, { isLoading: isCreating }] = useCreateCeoInflowPlanMutation();
  const [updateFact] = useUpdateCeoInflowFactMutation();

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editKs3Amount, setEditKs3Amount] = useState('');
  const [editKs3No, setEditKs3No] = useState('');
  const [editActualAmount, setEditActualAmount] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Поля нового плана БДДС / КС-3
  const [projectName, setProjectName] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [contractNo, setContractNo] = useState('');
  const [expectedDate, setExpectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [bddsPlanned, setBddsPlanned] = useState('');
  const [ks3Signed, setKs3Signed] = useState('');
  const [ks3DocNo, setKs3DocNo] = useState('');
  const [advanceDed, setAdvanceDed] = useState('');
  const [retentionDed, setRetentionDed] = useState('');
  const [actualReceived, setActualReceived] = useState('');
  const [responsible, setResponsible] = useState('');

  const availablePeriods = useMemo(() => {
    const set = new Set<string>();
    const now = new Date();
    for (let offset = -6; offset <= 3; offset += 1) {
      const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1));
      set.add(d.toISOString().slice(0, 10));
    }
    if (periodFilter) {
      set.add(periodFilter.slice(0, 10));
    }
    for (const r of data?.rows ?? []) {
      if (r.period_month) {
        set.add(r.period_month.slice(0, 10));
      }
    }
    return Array.from(set).sort().reverse();
  }, [data?.rows, periodFilter]);

  if (isLoading) {
    return <Card>Загрузка аналитики поступлений БДДС / КС-3 / Факт…</Card>;
  }

  if (isError || !data) {
    return (
      <Card>
        <ErrorRow>
          <span>Не удалось загрузить аналитику поступлений БДДС / КС-3 / Факт.</span>
          <Button $size="sm" $variant="secondary" onClick={() => refetch()}>
            Повторить
          </Button>
        </ErrorRow>
      </Card>
    );
  }

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!projectName.trim() || !customerName.trim()) return;
    const derivedPeriod =
      periodFilter || (expectedDate ? `${expectedDate.slice(0, 7)}-01` : undefined);
    try {
      setErrorMsg(null);
      await createInflow({
        period_month: derivedPeriod,
        project_name: projectName.trim(),
        customer_name: customerName.trim(),
        contract_number: contractNo.trim(),
        expected_payment_date: expectedDate || undefined,
        bdds_planned_amount: Number(bddsPlanned) || 0,
        ks2_submitted_amount: Number(ks3Signed) || Number(bddsPlanned) || 0,
        ks3_signed_amount: Number(ks3Signed) || 0,
        ks3_doc_number: ks3DocNo.trim() || undefined,
        advance_deduction: Number(advanceDed) || 0,
        retention_deduction: Number(retentionDed) || 0,
        actual_received_amount: Number(actualReceived) || 0,
        responsible_person: responsible.trim() || undefined,
      }).unwrap();
      setProjectName('');
      setCustomerName('');
      setBddsPlanned('');
      setKs3Signed('');
      setKs3DocNo('');
      setAdvanceDed('');
      setRetentionDed('');
      setActualReceived('');
      setShowAddForm(false);
    } catch {
      setErrorMsg('Не удалось сохранить позицию БДДС / КС-3. Проверьте данные и повторите.');
    }
  }

  async function handleSaveRowFact(rowId: string) {
    const parsedActual = editActualAmount.trim() === '' ? NaN : Number(editActualAmount);
    const parsedKs3 = editKs3Amount.trim() === '' ? NaN : Number(editKs3Amount);
    const newActual = Number.isFinite(parsedActual) && parsedActual >= 0 ? parsedActual : undefined;
    const newKs3 = Number.isFinite(parsedKs3) && parsedKs3 >= 0 ? parsedKs3 : undefined;

    try {
      setErrorMsg(null);
      await updateFact({
        id: rowId,
        ks3_signed_amount: newKs3,
        ks3_doc_number: editKs3No.trim(),
        actual_received_amount: newActual,
      }).unwrap();
      setEditingRowId(null);
    } catch {
      setErrorMsg('Не удалось обновить фактические данные по строке.');
    }
  }

  function handleStartEdit(row: CeoInflowPlanRow) {
    setEditingRowId(row.id);
    setEditKs3Amount(String(row.ks3_signed_amount));
    setEditKs3No(row.ks3_doc_number ?? '');
    setEditActualAmount(String(row.actual_received_amount));
  }

  return (
    <>
      {!editable && <TreasuryViewNote section="Поступления (КС-3)" to="/agents/finance/inflows" />}
      <Section>
        <HeaderRow>
          <div>
            <Title>
              Планируемые поступления по БДДС vs Ожидаемые по подписанным КС-3 vs Фактически
              поступившие
            </Title>
            <SubTitleText>
              Сквозная сверка трёх контуров денежных притоков: казначейский план БДДС, подписанные с
              Заказчиком справки о стоимости выполненных работ КС-3 (за вычетом аванса и ГУ) и
              фактический приход денег на р/с.
            </SubTitleText>
          </div>

          <HeaderActions>
            <Select
              value={periodFilter}
              onChange={(e) => setPeriodFilter(e.target.value)}
              aria-label="Период БДДС"
            >
              <option value="">Все периоды БДДС</option>
              {availablePeriods.map((pm) => (
                <option key={pm} value={pm}>
                  {formatPeriodMonthLabel(pm)}
                </option>
              ))}
            </Select>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">Все статусы поступлений</option>
              <option value="bdds_forecast">План БДДС (ожидается КС-2)</option>
              <option value="ks2_on_review">КС-2 на проверке у Заказчика</option>
              <option value="ks3_signed_awaiting">КС-3 подписана — ожидаем оплату</option>
              <option value="partially_paid">Частично оплачено по КС-3</option>
              <option value="overdue_receivable">Просрочка оплаты по КС-3</option>
              <option value="paid_in_full">Оплачено в полном объёме</option>
            </Select>
            {can_write && (
              <Button $size="sm" onClick={() => setShowAddForm((v) => !v)}>
                + Добавить план БДДС / КС-3
              </Button>
            )}
          </HeaderActions>
        </HeaderRow>

        {errorMsg && (
          <Card>
            <ErrorMessage>{errorMsg}</ErrorMessage>
          </Card>
        )}

        {can_write && showAddForm && (
          <AddInflowPlanModal
            projectName={projectName}
            setProjectName={setProjectName}
            customerName={customerName}
            setCustomerName={setCustomerName}
            contractNo={contractNo}
            setContractNo={setContractNo}
            expectedDate={expectedDate}
            setExpectedDate={setExpectedDate}
            bddsPlanned={bddsPlanned}
            setBddsPlanned={setBddsPlanned}
            ks3Signed={ks3Signed}
            setKs3Signed={setKs3Signed}
            ks3DocNo={ks3DocNo}
            setKs3DocNo={setKs3DocNo}
            advanceDed={advanceDed}
            setAdvanceDed={setAdvanceDed}
            retentionDed={retentionDed}
            setRetentionDed={setRetentionDed}
            actualReceived={actualReceived}
            setActualReceived={setActualReceived}
            responsible={responsible}
            setResponsible={setResponsible}
            isCreating={isCreating}
            onSubmit={handleCreate}
            onClose={() => setShowAddForm(false)}
          />
        )}

        <InflowsFunnelGrid
          totalBddsPlanned={data.total_bdds_planned}
          totalKs2Submitted={data.total_ks2_submitted}
          bddsToKs3Gap={data.bdds_to_ks3_gap}
          totalExpectedNetKs3={data.total_expected_net_ks3}
          totalKs3SignedGross={data.total_ks3_signed_gross}
          totalAdvanceDeduction={data.total_advance_deduction}
          totalRetentionDeduction={data.total_retention_deduction}
          totalActualReceived={data.total_actual_received}
          bddsFulfillmentPct={data.bdds_fulfillment_pct}
          ks3CollectionPct={data.ks3_collection_pct}
          ks3AwaitingCash={data.ks3_awaiting_cash}
          overdueKs3Receivables={data.overdue_ks3_receivables}
        />
      </Section>

      <InflowsTable
        rows={data.rows}
        editingRowId={editingRowId}
        editKs3Amount={editKs3Amount}
        setEditKs3Amount={setEditKs3Amount}
        editKs3No={editKs3No}
        setEditKs3No={setEditKs3No}
        editActualAmount={editActualAmount}
        setEditActualAmount={setEditActualAmount}
        can_write={can_write}
        onStartEdit={handleStartEdit}
        onCancelEdit={() => setEditingRowId(null)}
        onSaveRowFact={handleSaveRowFact}
      />

      <SmetaKs3ActsTable acts={data.smeta_ks3_acts} />
    </>
  );
}
